import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { performanceApi } from '@/api/performance'
import type { PerformanceDetailItem } from '@/types/performance'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { DateRangePicker } from '@/components/DateRangePicker'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, TrendingUp } from 'lucide-react'
import { ListPagination, DEFAULT_PAGE_SIZE } from '@/components/common/ListPagination'

const formatMoney = (v: number) => `¥${(v ?? 0).toFixed(2)}`

// 可排序字段（与后端 performanceSortColumns 白名单对应，creator_name 等非表字段不支持）
type SortField = 'order_no' | 'customer_phone' | 'pay_price' | 'cut_promotion' | 'cut_cost' | 'cut_marketing' | 'cut_warehouse' | 'net_performance' | 'created_at'
type SortDirection = 'asc' | 'desc'

const sortFields: { field: SortField | null; label: string }[] = [
  { field: 'order_no', label: '订单号' },
  { field: null, label: '员工' },
  { field: 'customer_phone', label: '客户手机' },
  { field: 'pay_price', label: '订单金额' },
  { field: 'cut_promotion', label: '推广扣款' },
  { field: 'cut_cost', label: '本金扣款' },
  { field: 'cut_marketing', label: '促销扣款' },
  { field: 'cut_warehouse', label: '仓管扣款' },
  { field: 'net_performance', label: '净业绩' },
  { field: null, label: '类型' },
  { field: 'created_at', label: '时间' },
]

export function PromotionPerformancePage() {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const defaultRange = {
    start_date: firstDay.toISOString().slice(0, 10),
    end_date: today.toISOString().slice(0, 10),
  }
  const [dateRange, setDateRange] = useState(defaultRange)
  const [orderNo, setOrderNo] = useState('')
  const [queryState, setQueryState] = useState({
    ...defaultRange,
    order_no: '',
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
    setPage(1)
  }

  const { data, isLoading } = useQuery({
    queryKey: ['promotion-detail', queryState, page, pageSize, sortField, sortDirection],
    queryFn: () => performanceApi.getPromotionDetail({
      page,
      page_size: pageSize,
      sort_by: sortField,
      sort_order: sortDirection,
      ...queryState,
    }),
  })

  const SortIcon = ({ field }: { field: SortField }) => {
    if (field !== sortField) return <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 opacity-40" />
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 inline h-3.5 w-3.5" />
      : <ArrowDown className="ml-1 inline h-3.5 w-3.5" />
  }

  const handleSearch = () => {
    setPage(1)
    setQueryState({ ...dateRange, order_no: orderNo })
  }

  const list = data?.list ?? []
  const total = data?.total ?? 0

  // 计算当页汇总
  const pageSummary = list.reduce((acc, item) => {
    acc.pay_price += item.pay_price ?? 0
    acc.cut_promotion += item.cut_promotion ?? 0
    acc.cut_cost += item.cut_cost ?? 0
    acc.cut_marketing += item.cut_marketing ?? 0
    acc.cut_warehouse += item.cut_warehouse ?? 0
    acc.net_performance += item.net_performance ?? 0
    return acc
  }, { pay_price: 0, cut_promotion: 0, cut_cost: 0, cut_marketing: 0, cut_warehouse: 0, net_performance: 0 })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <TrendingUp className="h-8 w-8" />
          推广业绩明细
        </h1>
        <p className="text-muted-foreground mt-1">订单级推广扣款和员工到手业绩</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>筛选条件</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1 w-56">
              <Label className="text-sm">日期范围</Label>
              <DateRangePicker
                value={dateRange}
                onChange={(v) => setDateRange({ start_date: v.start_date ?? '', end_date: v.end_date ?? '' })}
                className="w-full h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">订单号</Label>
              <Input
                placeholder="输入订单号搜索"
                value={orderNo}
                onChange={e => setOrderNo(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="w-56"
              />
            </div>
            <Button onClick={handleSearch} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              查询
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {sortFields.map(({ field, label }, index) => (
                  <TableHead
                    key={label}
                    className={index >= 3 && index <= 8 ? 'text-right' : undefined}
                  >
                    {field ? (
                      <button
                        type="button"
                        className="inline-flex items-center hover:text-foreground"
                        onClick={() => handleSort(field)}
                        aria-label={`按${label}排序`}
                      >
                        {label}
                        <SortIcon field={field} />
                      </button>
                    ) : (
                      label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-muted/50">
                <TableCell colSpan={3} className="font-bold">当页汇总</TableCell>
                <TableCell className="text-right font-bold">{formatMoney(pageSummary.pay_price)}</TableCell>
                <TableCell className="text-right">{formatMoney(pageSummary.cut_promotion)}</TableCell>
                <TableCell className="text-right">{formatMoney(pageSummary.cut_cost)}</TableCell>
                <TableCell className="text-right">{formatMoney(pageSummary.cut_marketing)}</TableCell>
                <TableCell className="text-right">{formatMoney(pageSummary.cut_warehouse)}</TableCell>
                <TableCell className="text-right font-bold">{formatMoney(pageSummary.net_performance)}</TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
              {list.map((item: PerformanceDetailItem) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-xs">{item.order_no}</TableCell>
                  <TableCell>{item.creator_name || '—'}</TableCell>
                  <TableCell>{item.customer_phone}</TableCell>
                  <TableCell className="text-right">{formatMoney(item.pay_price)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatMoney(item.cut_promotion)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatMoney(item.cut_cost)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatMoney(item.cut_marketing)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatMoney(item.cut_warehouse)}</TableCell>
                  <TableCell className="text-right font-bold">{formatMoney(item.net_performance)}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_new ? 'default' : 'secondary'}>
                      {item.is_new ? '新开' : '复购'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(item.created_at).toLocaleString('zh-CN')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && list.length === 0 && (
            <p className="text-center text-muted-foreground py-8">暂无数据</p>
          )}
        </CardContent>
      </Card>

      <ListPagination
        page={page}
        pageSize={pageSize}
        total={total}
        loading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={(ps) => { setPageSize(ps); setPage(1) }}
      />
    </div>
  )
}
